/** @format */

import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
	HelpCircle,
	ChevronDown,
	Search,
	Info,
	ArrowRight,
	ShoppingCart,
	Truck,
	Wrench,
	ShieldCheck,
	Ruler,
	Palette,
} from "lucide-react"; // Use HelpCircle as main icon
import ContactBlock from "../components/contact/ContactBlock";
import ReactMarkdown from "react-markdown";

// Components

// --- FAQ Data Structure ---
// Group questions by category for better organization
const faqData = [
	// 1. General / About Us
	{
		category: "General & About Us",
		id: "general",
		icon: Info,
		questions: [
			{
				id: "q101",
				question: "Are your shades made in Canada?",
				answer: (
					<>
						Yes! We proudly manufacture all our custom window shades right here
						in our facility in{" "}
						<strong className="font-medium">Toronto, Ontario</strong>. Choosing
						Off Cut Shades supports local Canadian craftsmanship. Learn more on
						our{" "}
						<Link to="/about" className="text-primary hover:underline">
							About Us
						</Link>{" "}
						page.
					</>
				),
			},
			{
				id: "q102",
				question: "What makes Off Cut Shades different?",
				answer:
					"We focus on simplifying the custom shade experience. We offer high-quality, Canadian-made products with incredibly fast **2-business day processing**, easy customization options, free samples, and dedicated expert support.",
			},
			{
				id: "q103",
				question: "Do you have a showroom I can visit?",
				answer: (
					<>
						Yes, we do! Our showroom is located at{" "}
						<strong className="font-medium">
							50 Hollinger Road, Toronto, ON
						</strong>
						. Walk-ins are welcome during business hours (Mon-Fri, 8:30 AM -
						5:00 PM ET). You can find hours and a map on our{" "}
						<Link to="/contact" className="text-primary hover:underline">
							Contact Us
						</Link>{" "}
						page. You can also book a consultation online!
					</>
				),
			},
		],
	},
	// 2. Products & Fabrics
	{
		category: "Products & Fabrics",
		id: "products",
		icon: Palette, // Using Palette icon from selection guide
		questions: [
			{
				id: "q201",
				question: "What types of window shades do you offer?",
				answer: (
					<>
						We specialize in high-quality custom Roller Shades, Zebra Shades,
						Cellular Shades, and Sheer Shades. You can explore all types on our{" "}
						<Link to="/shop" className="text-primary hover:underline">
							Shop
						</Link>{" "}
						page.
					</>
				),
			},
			{
				id: "q202",
				question:
					"What's the difference between Blackout, Privacy, Light Filtering, Solar, and Sheer fabrics?",
				answer: (
					<>
						Great question! Each type offers different levels of light control
						and privacy. <br />
						<br />
						<strong className="font-medium">Blackout</strong> blocks 100% light.{" "}
						<br />
						<strong className="font-medium">Privacy/Room Darkening</strong>{" "}
						significantly darkens and offers full privacy.
						<br />
						<strong className="font-medium">
							Light Filtering (Translucent)
						</strong>{" "}
						softly diffuses light with moderate privacy.
						<br />
						<strong className="font-medium">Solar/Sunscreen</strong> reduces
						heat/glare while preserving view (daytime privacy).
						<br />
						<strong className="font-medium">Sheer/Transparent</strong> maximizes
						light and view with minimal privacy.
						<br /> <br />
						See our{" "}
						<Link
							to="/guides/productselection"
							className="text-primary hover:underline"
						>
							Fabric Selection Guide
						</Link>{" "}
						for detailed comparisons and room recommendations.
					</>
				),
			},
			{
				id: "q203",
				question: "How can I be sure about the fabric colour and texture?",
				answer: (
					<>
						Screen colors can vary! The absolute best way to ensure you love the
						color and feel is to order{" "}
						<strong className="font-medium">free fabric samples</strong>. You
						can select up to 5 samples, and we'll ship them right to you. Visit
						our{" "}
						<Link to="/swatches" className="text-primary hover:underline">
							Free Samples
						</Link>{" "}
						page to order.
					</>
				),
			},
			{
				id: "q204",
				question: "Do you offer motorized/automated shades?",
				answer: (
					<>
						Yes! We offer premium rechargeable motors with remote, app, and
						smart home integration options (via our Connector+ Hub). Learn all
						about the benefits and how to order on our{" "}
						<Link
							to="/motorized-solutions"
							className="text-primary hover:underline"
						>
							Motorization
						</Link>{" "}
						page.
					</>
				),
			},
		],
	},
	// 3. Measuring & Customization
	{
		category: "Measuring & Customization",
		id: "measuring",
		icon: Ruler, // Using Ruler icon
		questions: [
			{
				id: "q301",
				question: "How do I measure my windows accurately?",
				answer: (
					<>
						Accurate measurements are key! We have a simple, step-by-step guide
						that explains exactly how to measure for both{" "}
						<strong className="font-medium">Inside Mount</strong> and{" "}
						<strong className="font-medium">Outside Mount</strong>. We recommend
						using a metal tape measure. Check out the full{" "}
						<Link
							to="/guides/measuring-guide"
							className="text-primary hover:underline"
						>
							Measuring Guide
						</Link>{" "}
						and download our worksheet.
					</>
				),
			},
			{
				id: "q302",
				question:
					"What's the difference between Inside Mount and Outside Mount?",
				answer: (
					<>
						<strong className="font-medium">Inside Mount</strong> fits within
						the window frame for a clean look (requires sufficient depth, we
						make a small deduction for clearance).{" "}
						<strong className="font-medium">Outside Mount</strong> hangs
						outside/above the frame, providing better light blockage and
						covering imperfections (we make the shade to your exact specified
						size). Our{" "}
						<Link
							to="/guides/measuring-guide#mount-type"
							className="text-primary hover:underline"
						>
							Measuring Guide
						</Link>{" "}
						explains this in detail.
					</>
				),
			},
			{
				id: "q303",
				question: "What if I make a mistake measuring?",
				answer: (
					<>
						We understand mistakes happen! That's why we offer our{" "}
						<strong className="font-medium">
							SureFit™ Measurement Guarantee
						</strong>
						. If you mis-measure, contact us within{" "}
						<strong className="font-medium">14 days</strong> of receiving your
						order, and we'll work with you on the best, most cost-effective
						solution, often involving remaking the shade at a significant
						discount. See our{" "}
						<Link to="/help/guarantee" className="text-primary hover:underline">
							Guarantees Page
						</Link>{" "}
						for details.
					</>
				),
			},
			{
				id: "q304",
				question: "How does the online customizer work?",
				answer: (
					<>
						Our customizer guides you step-by-step. You'll start by entering
						your <strong className="font-medium">Details</strong> (width,
						height, mount), then choose your
						<strong className="font-medium">Fabric</strong>, and depending on
						the shade type, select{" "}
						<strong className="font-medium">Bottom Bar</strong>,
						<strong className="font-medium">Control</strong> (including
						Motorization), and <strong className="font-medium">Valance</strong>{" "}
						options. A final <strong className="font-medium">Summary</strong>{" "}
						lets you review before adding to the cart. Each step provides info
						to help you choose.
					</>
				),
			},
		],
	},
	// 4. Ordering & Samples
	{
		category: "Ordering & Samples",
		id: "ordering",
		icon: ShoppingCart, // Using ShoppingCart icon
		questions: [
			{
				id: "q401",
				question: "Are fabric samples really free?",
				answer:
					"Yes! You can order up to **5 fabric samples completely free**, including standard shipping within Canada. This is the best way to see colours and textures in your own home. Express shipping for samples is available for a fee (refundable with a shade purchase).",
			},
			{
				id: "q402",
				question: "How do I order samples?",
				answer: (
					<>
						Visit our{" "}
						<Link to="/swatches" className="text-primary hover:underline">
							Free Samples
						</Link>{" "}
						page. Browse the fabrics, select the ones you like (up to 5), add
						them to your sample cart, and proceed through the simple checkout
						process (no payment needed for standard shipping).
					</>
				),
			},
			{
				id: "q403",
				question: "What payment methods do you accept?",
				answer:
					"We accept major credit cards (Visa, MasterCard, American Express) processed securely via Stripe, as well as PayPal. For Canadian customers, Affirm financing may also be available during checkout.",
			},
			{
				id: "q404",
				question: "Is my online payment secure?",
				answer:
					"Absolutely. We use industry-standard encryption (SSL) to protect your connection. All payments are processed securely through trusted third-party providers like Stripe and PayPal. We do not store your full credit card details on our servers.",
			},
		],
	},
	// 5. Shipping & Delivery
	{
		category: "Shipping & Delivery",
		id: "shipping",
		icon: Truck, // Using Truck icon
		questions: [
			{
				id: "q501",
				question: "How much does shipping cost?",
				answer:
					"We offer **FREE standard shipping** on all custom shade orders across Canada. Shipping costs for orders to the USA are calculated automatically at checkout based on size, weight, and destination. Please note, US customers are responsible for any applicable duties or taxes.",
			},
			{
				id: "q502",
				question: "How long will it take to get my order?",
				answer: (
					<>
						We have a fast{" "}
						<strong className="font-medium">
							2-business day processing time
						</strong>{" "}
						in our Toronto factory. After that, standard shipping typically
						takes{" "}
						<strong className="font-medium">3-9 business days in Canada</strong>{" "}
						and{" "}
						<strong className="font-medium">
							5-10 business days to the USA
						</strong>
						(estimates may vary). You'll receive tracking information once your
						order ships. See our{" "}
						<Link
							to="/help/shipping-policy"
							className="text-primary hover:underline"
						>
							Shipping Policy
						</Link>{" "}
						for details.
					</>
				),
			},
			{
				id: "q503",
				question: "What if my order arrives damaged?",
				answer: (
					<>
						Please inspect your package upon arrival. If you notice any damage
						to the box or shade, take photos and contact us at{" "}
						<a
							href="mailto:info@offcutshades.com"
							className="text-primary hover:underline"
						>
							info@offcutshades.com
						</a>{" "}
						within <strong className="font-medium">7 days</strong> of delivery.
						We need this prompt notification and photo evidence to process a
						claim. See our{" "}
						<Link
							to="/help/shipping-policy"
							className="text-primary hover:underline"
						>
							Shipping Policy
						</Link>{" "}
						for details.
					</>
				),
			},
		],
	},
	// 6. Installation
	{
		category: "Installation",
		id: "installation",
		icon: Wrench, // Using Wrench icon
		questions: [
			{
				id: "q601",
				question: "Are the shades easy to install myself?",
				answer:
					"Yes! Our shades are designed for straightforward DIY installation. In most cases, you'll only need basic tools like a drill, screwdriver, pencil, and tape measure. We provide all necessary mounting hardware and instructions.",
			},
			{
				id: "q602",
				question: "Where can I find installation instructions?",
				answer: (
					<>
						You can find detailed, step-by-step instructions for each product
						type on our{" "}
						<Link
							to="/guides/installation-guide"
							className="text-primary hover:underline"
						>
							Installation Guides
						</Link>{" "}
						page. Please download and read the guide specific to your shade{" "}
						<strong className="font-medium">before</strong> starting.
					</>
				),
			},
		],
	},
	// 7. Warranty & Returns
	{
		category: "Warranty & Returns",
		id: "warranty",
		icon: ShieldCheck, // Using ShieldCheck icon
		questions: [
			{
				id: "q701",
				question: "What is your return policy?",
				answer:
					"Because each shade is custom-made to your exact specifications, we generally cannot accept returns or exchanges for reasons like change of mind or incorrect measurements (but see our SureFit™ Guarantee!). However, your satisfaction is paramount.",
			},
			{
				id: "q702",
				question: "What guarantees do you offer?",
				answer: (
					<>
						We offer several guarantees for your peace of mind: a{" "}
						<strong className="font-medium">
							SureFit™ Measurement Guarantee*
						</strong>{" "}
						(help if you mis-measure), a{" "}
						<strong className="font-medium">
							Quality & Manufacturing Guarantee
						</strong>{" "}
						(against defects), and a{" "}
						<strong className="font-medium">Satisfaction Guarantee</strong>.
						Please read our full{" "}
						<Link to="/help/guarantee" className="text-primary hover:underline">
							Guarantees Page
						</Link>{" "}
						for details and timeframes (usually 14 days for
						measurement/satisfaction issues).
					</>
				),
			},
			{
				id: "q703",
				question: "What does the 5-Year Warranty cover?",
				answer: (
					<>
						Our comprehensive{" "}
						<strong className="font-medium">5-Year Warranty</strong> covers
						manufacturing defects in materials and workmanship under normal use.
						It doesn't cover normal wear and tear, accidents, or misuse. Full
						details are available on the{" "}
						<Link to="/help/warranty" className="text-primary hover:underline">
							Warranty Page
						</Link>
						.
					</>
				),
			},
			{
				id: "q704",
				question: "How do I make a warranty or guarantee claim?",
				answer: (
					<>
						If you believe you have an issue covered by our guarantees or
						warranty, please{" "}
						<strong className="font-medium">
							contact our Customer Service team
						</strong>{" "}
						promptly with your order number, a description of the issue, and
						clear photos/videos. Visit our{" "}
						<Link to="/contact" className="text-primary hover:underline">
							Contact Us
						</Link>{" "}
						page for ways to reach us.
					</>
				),
			},
		],
	},
];

// --- Accordion Item Component ---
const AccordionItem = ({ id, question, answer, isOpen, onToggle }) => (
	<div className="px-4 border-b border-gray-200 last:border-b-0">
		<h2>
			<button
				type="button"
				className="flex justify-between items-center w-full py-4 px-1 text-left font-semibold text-gray-800 hover:text-primary transition-colors"
				onClick={() => onToggle(id)}
				aria-expanded={isOpen}
				aria-controls={`faq-panel-${id}`}
			>
				<span>{question}</span>
				<ChevronDown
					className={`w-5 h-5 transform transition-transform duration-200 ${
						isOpen ? "rotate-180" : ""
					}`}
				/>
			</button>
		</h2>
		<AnimatePresence initial={false}>
			{isOpen && (
				<motion.div
					id={`faq-panel-${id}`}
					initial={{ height: 0, opacity: 0 }}
					animate={{ height: "auto", opacity: 1 }}
					exit={{ height: 0, opacity: 0 }}
					transition={{ duration: 0.3, ease: "easeInOut" }}
					className="overflow-hidden"
				>
					{/* Add padding to the content area */}
					<div className="pb-4 pt-1 px-1 text-sm text-gray-700 leading-relaxed space-y-3">
						{/* Render answer - assumes answer is JSX or string */}
						{typeof answer === "string" ? (
							<ReactMarkdown>{answer}</ReactMarkdown>
						) : (
							answer
						)}
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	</div>
);

// --- Main FAQ Screen Component ---
const FAQScreen = () => {
	const [openQuestionId, setOpenQuestionId] = useState(null); // State for accordion

	const handleToggle = (id) => {
		setOpenQuestionId((prevId) => (prevId === id ? null : id)); // Toggle open/close
	};

	return (
		<div className="overflow-x-hidden bg-gray-50">
			<Helmet>
				<title>FAQ - Frequently Asked Questions | Off Cut Shades</title>
				<meta
					name="description"
					content="Find answers to common questions about Off Cut Shades products, measuring, ordering, shipping, installation, warranty, and more. Get help with your custom window shades."
				/>
				<link rel="canonical" href="https://offcutshades.com/help/faq" />{" "}
				{/* Assuming FAQ path */}
			</Helmet>

			<main className="pt-24 pb-16 md:pt-32 md:pb-24">
				{/* --- Hero Section --- */}
				<section className="container mx-auto px-4 text-center mb-12 md:mb-16">
					<motion.div
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
						className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4"
					>
						<HelpCircle size={36} strokeWidth={1.5} />
					</motion.div>
					<motion.h1
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
					>
						Frequently Asked Questions
					</motion.h1>
					<motion.p
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.3 }}
						className="text-lg text-gray-600 max-w-2xl mx-auto"
					>
						Find quick answers to common questions. If you don't see your
						question here, please contact us!
					</motion.p>
					{/* Optional Search Bar */}
					{/* <div className="mt-8 max-w-lg mx-auto relative"> ... search input ... </div> */}
				</section>

				{/* --- FAQ Sections --- */}
				<section className="container mx-auto px-4 max-w-3xl mx-auto">
					{faqData.map((category) => (
						<motion.div
							key={category.id}
							className="mb-10 md:mb-12"
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.2 }}
							transition={{ duration: 0.5 }}
						>
							{/* Category Title */}
							<h2 className="text-2xl font-semibold text-gray-800 mb-5 pb-2 border-b border-gray-200 flex items-center gap-2">
								{React.createElement(category.icon, {
									size: 22,
									className: "text-primary opacity-80",
								})}
								{category.category}
							</h2>
							{/* Accordion Items */}
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
								{category.questions.map((qna) => (
									<AccordionItem
										key={qna.id}
										id={qna.id}
										question={qna.question}
										answer={qna.answer}
										isOpen={openQuestionId === qna.id}
										onToggle={handleToggle}
									/>
								))}
							</div>
						</motion.div>
					))}
				</section>
			</main>

			{/* --- Contact Block --- */}
			<ContactBlock
				title="Didn't Find Your Answer?"
				description="Our friendly support team is here to help with any questions you may have."
			/>
		</div>
	);
};

export default FAQScreen;
